import http from '../http'

/**
 * 创建物流信息
 * @param {Object} data - 物流信息数据
 * @param {Number} data.orderId - 关联订单ID
 * @param {String} data.trackingNumber - 物流单号
 * @param {String} data.logisticsCompany - 物流公司名称
 * @param {String} [data.logisticsCompanyCode] - 物流公司编码
 * @param {String} [data.shipTime] - 发货时间
 * @param {String} [data.estimatedArrivalTime] - 预计到达时间
 * @param {String} [data.shipAddress] - 发货地址
 * @param {String} [data.receiveAddress] - 收货地址
 * @param {String} [data.shipperName] - 发货人姓名
 * @param {String} [data.shipperPhone] - 发货人电话
 * @param {String} [data.receiverName] - 收货人姓名
 * @param {String} [data.receiverPhone] - 收货人电话
 * @param {String} [data.logisticsStatus] - 物流状态（默认为"1"已发货）
 * @param {String} [data.logisticsStatusDesc] - 物流状态描述
 * @param {String} [data.remark] - 备注信息
 * @returns {Promise} - 返回创建结果的Promise对象
 */
export function create_logistics(data) {
// 设置默认值
  const logisticsData = {
    logisticsStatus: '1', // 默认为已发货
    logisticsStatusDesc: '已发货',
    ...data
  }
  
  return http.post('/logistics', logisticsData)
}

/**
 * 更新物流信息
 * @param {Number} id - 物流信息ID
 * @param {Object} data - 物流信息数据
 * @returns {Promise} - 返回更新结果的Promise对象
 */
export function update_logistics(id, data) {
return http.put(`/logistics/${id}`, data)
}

/**
 * 根据ID获取物流信息
 * @param {Number} id - 物流信息ID
 * @returns {Promise} - 返回物流信息的Promise对象
 */
export function get_logistics_by_id(id) {
return http.get(`/logistics/${id}`)
}

/**
 * 获取送货单固定信息；历史记录首次调用时生成并保存送货单编号。
 */
export function get_or_create_delivery_note(id) {
return http.post(`/logistics/${id}/delivery-note`)
}

/**
 * 根据订单ID获取物流信息列表
 * @param {Number} orderId - 订单ID
 * @returns {Promise} - 返回物流信息列表的Promise对象
 */
export function get_logistics_by_order_id(orderId) {
return http.get(`/logistics/order/${orderId}`)
}

/**
 * 根据订单ID获取所有发货清单信息（包含物流信息和发货明细）
 * @param {Number} orderId - 订单ID
 * @returns {Promise} - 返回发货清单信息的Promise对象
 */
export function get_order_shipments_with_details(orderId) {
return http.get(`/logistics/order/${orderId}/shipments`)
}

/**
 * 分页查询物流信息
 * @param {Object} params - 查询参数
 * @param {Number} [params.orderId] - 订单ID
 * @param {String} [params.trackingNumber] - 物流单号（模糊查询）
 * @param {String} [params.logisticsCompany] - 物流公司（模糊查询）
 * @param {String} [params.logisticsStatus] - 物流状态
 * @param {Boolean} [params.isReceived] - 是否已签收
 * @param {Number} [params.creatorId] - 创建者ID
 * @param {String} [params.status] - 记录状态
 * @param {Number} [params.page=0] - 页码（默认0）
 * @param {Number} [params.size=10] - 每页大小（默认10）
 * @returns {Promise} - 返回物流信息列表及分页信息的Promise对象
 */
export function query_logistics(params = {}) {
  // 处理分页参数，后端使用page/size格式
  const queryParams = {
    orderId: params.orderId,
    trackingNumber: params.trackingNumber,
    logisticsCompany: params.logisticsCompany,
    logisticsStatus: params.logisticsStatus,
    isReceived: params.isReceived,
    creatorId: params.creatorId,
    status: params.status,
    page: params.page || 0,
    size: params.size || 10
  }
  
  // 移除undefined值
  Object.keys(queryParams).forEach(key => {
    if (queryParams[key] === undefined) {
      delete queryParams[key]
    }
  })
  
return http.get('/logistics', { params: queryParams })
}

/**
 * 删除物流信息
 * @param {Number} id - 物流信息ID
 * @returns {Promise} - 返回删除结果的Promise对象
 */
export function delete_logistics(id) {
return http.delete(`/logistics/${id}`)
}

/**
 * 更新物流状态
 * @param {Number} id - 物流信息ID
 * @param {String} status - 物流状态（1-5）
 * @param {String} [statusDesc] - 状态描述
 * @returns {Promise} - 返回更新结果的Promise对象
 */
export function update_logistics_status(id, status, statusDesc) {
return http.put(`/logistics/${id}/status`, null, { 
    params: { 
      status, 
      statusDesc 
    } 
  })
}

/**
 * 签收物流信息
 * @param {Number} id - 物流信息ID
 * @param {String} receivedBy - 签收人
 * @param {String} [receivedPhone] - 签收人电话
 * @param {String} [receivedCertificate] - 签收凭证
 * @param {String} [receivedTime] - 收货日期（格式：yyyy-MM-dd HH:mm:ss）
 * @returns {Promise} - 返回签收结果的Promise对象
 */
export function receive_logistics(id, receivedBy, receivedPhone, receivedCertificate, receivedTime) {
// 构建参数对象，只包含有值的参数
  const params = { receivedBy };
  
  if (receivedPhone) {
    params.receivedPhone = receivedPhone;
  }
  
  if (receivedCertificate) {
    params.receivedCertificate = receivedCertificate;
  }
  
  if (receivedTime) {
    // 后端可能期望 ISO 格式，将 yyyy-MM-dd HH:mm:ss 转为 yyyy-MM-ddTHH:mm:ss
    params.receivedTime = receivedTime.replace(' ', 'T');
  }
  
  return http.put(`/logistics/${id}/receive`, null, { params })
}

/**
 * 修改签收时间（仅已签收的物流信息可修改）
 * @param {Number} id - 物流信息ID
 * @param {String} receivedTime - 新的签收时间（ISO 8601 格式，如 2024-01-02T18:30:00）
 * @returns {Promise} - 返回修改结果的Promise对象
 */
export function update_logistics_received_time(id, receivedTime) {
return http.put(`/logistics/${id}/received-time`, null, {
    params: { receivedTime }
  })
}

/**
 * 获取即将到达的物流信息
 * @param {Number} [days=7] - 未来天数（默认7天）
 * @returns {Promise} - 返回即将到达物流信息的Promise对象
 */
export function get_upcoming_logistics(days = 7) {
return http.get('/logistics/upcoming', { params: { days } })
}

/**
 * 获取超时未签收的物流信息
 * @returns {Promise} - 返回超时未签收物流信息的Promise对象
 */
export function get_overdue_logistics() {
return http.get('/logistics/overdue')
}

/**
 * 根据物流单号查询物流信息
 * @param {String} trackingNumber - 物流单号
 * @returns {Promise} - 返回物流信息的Promise对象
 */
export function get_logistics_by_tracking_number(trackingNumber) {
return http.get(`/logistics/tracking/${trackingNumber}`)
}

/**
 * 批量创建物流信息和发货清单
 * @param {Object} data - 批量创建数据
 * @param {Object} data.logisticsInfo - 物流信息
 * @param {Array} data.shipmentItems - 发货清单项数组
 * @returns {Promise} - 返回创建结果的Promise对象
 */
export function create_logistics_with_shipment(data) {
return http.post('/logistics/with-shipment', data)
}

// ========== 发货清单相关API ==========

/**
 * 创建发货清单项
 * @param {Object} data - 发货清单数据
 * @param {Number} data.sequenceNumber - 序号
 * @param {Number} data.logisticsInfoId - 关联物流信息ID
 * @param {Number} [data.orderListId] - 关联订单清单ID
 * @param {String} data.productName - 材料名称
 * @param {String} [data.specification] - 规格型号
 * @param {String} [data.materialRequirement] - 材质要求
 * @param {String} [data.unit] - 单位
 * @param {Number} data.shippedQuantity - 发货数量
 * @param {String} data.pricingMethod - 计价方式（FIXED/FLOATING）
 * @param {Number} [data.taxExclusiveUnitPrice] - 不含税单价
 * @param {Number} [data.taxInclusiveUnitPrice] - 含税单价
 * @param {Number} [data.taxInclusiveBasePrice] - 含税固定价
 * @param {Number} [data.taxInclusiveFloatingPrice] - 含税变动价
 * @param {Number} [data.taxInclusiveTotalAmount] - 含税合价
 * @param {Number} [data.taxRate] - 税率
 * @param {String} [data.remark] - 备注
 * @param {String} [data.shipmentDate] - 发货日期
 * @param {Number} [data.productSupplierId] - 产品供应商ID
 * @returns {Promise} - 返回创建结果的Promise对象
 */
export function create_shipment_list_item(data) {
return http.post('/shipment-list', data)
}

/**
 * 批量创建发货清单项
 * @param {Array} items - 发货清单项数组
 * @returns {Promise} - 返回创建结果的Promise对象
 */
export function create_shipment_list_items(items) {
return http.post('/shipment-list/batch', { items })
}

/**
 * 根据物流信息ID获取发货清单
 * @param {Number} logisticsInfoId - 物流信息ID
 * @returns {Promise} - 返回发货清单列表的Promise对象
 */
export function get_shipment_list_by_logistics_id(logisticsInfoId) {
return http.get(`/shipment-list/logistics/${logisticsInfoId}`)
}

/**
 * 根据订单清单ID获取发货清单
 * @param {Number} orderListId - 订单清单ID
 * @returns {Promise} - 返回发货清单列表的Promise对象
 */
export function get_shipment_list_by_order_list_id(orderListId) {
return http.get(`/shipment-list/order-list/${orderListId}`)
}

/**
 * 分页查询发货清单
 * @param {Object} params - 查询参数
 * @param {Number} [params.page=0] - 页码（默认0）
 * @param {Number} [params.size=10] - 每页大小（默认10）
 * @returns {Promise} - 返回发货清单列表及分页信息的Promise对象
 */
export function query_shipment_list(params = {}) {
  const queryParams = {
    page: params.page || 0,
    size: params.size || 10
  }
  
return http.get('/shipment-list', { params: queryParams })
}

/**
 * 根据物流信息ID分页查询发货清单
 * @param {Number} logisticsInfoId - 物流信息ID
 * @param {Object} params - 查询参数
 * @param {Number} [params.page=0] - 页码（默认0）
 * @param {Number} [params.size=10] - 每页大小（默认10）
 * @returns {Promise} - 返回发货清单列表及分页信息的Promise对象
 */
export function query_shipment_list_by_logistics_id(logisticsInfoId, params = {}) {
  const queryParams = {
    page: params.page || 0,
    size: params.size || 10
  }
  
return http.get(`/shipment-list/logistics/${logisticsInfoId}/page`, { params: queryParams })
}

/**
 * 删除发货清单项
 * @param {Number} id - 发货清单项ID
 * @returns {Promise} - 返回删除结果的Promise对象
 */
export function delete_shipment_list_item(id) {
return http.delete(`/shipment-list/${id}`)
}

/**
 * 根据物流信息ID删除所有发货清单
 * @param {Number} logisticsInfoId - 物流信息ID
 * @returns {Promise} - 返回删除结果的Promise对象
 */
export function delete_shipment_list_by_logistics_id(logisticsInfoId) {
return http.delete(`/shipment-list/logistics/${logisticsInfoId}`)
}

export default {
  create_logistics,
  update_logistics,
  update_logistics_received_time,
  get_logistics_by_id,
  get_or_create_delivery_note,
  get_logistics_by_order_id,
  get_order_shipments_with_details,
  query_logistics,
  delete_logistics,
  update_logistics_status,
  receive_logistics,
  get_upcoming_logistics,
  get_overdue_logistics,
  get_logistics_by_tracking_number,
  create_logistics_with_shipment,
  // 发货清单相关
  create_shipment_list_item,
  create_shipment_list_items,
  get_shipment_list_by_logistics_id,
  get_shipment_list_by_order_list_id,
  query_shipment_list,
  query_shipment_list_by_logistics_id,
  delete_shipment_list_item,
  delete_shipment_list_by_logistics_id
}
